import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "design-system/atoms/Collapsible"
import { cn } from "design-system/utils"
import { ChevronDown, Layers } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ConversationResponseDataSchema } from "schema"
import z from "zod"
import { CapabilityExecutionMarker } from "./CapabilityExecutionMarker"

type Execution = NonNullable<
  z.infer<typeof ConversationResponseDataSchema>["conversationCapabilityExecutions"]
>[number]

type Props = {
  executions: Execution[]
}

export function CapabilityExecutionsGroup({ executions }: Props) {
  const { t } = useTranslation()

  const { errorCount, unsuccessCount, skippedCount, textColorClass, borderColorClass } =
    useMemo(() => {
      const errors = executions.filter((execution) => execution.status === "Error").length
      const unsuccess = executions.filter((execution) => execution.status === "Unsuccess").length
      const skipped = executions.filter((execution) => execution.status === "Skipped").length
      const succeeded = executions.filter((execution) => execution.status === "Success").length

      if (errors > 0)
        return {
          errorCount: errors,
          unsuccessCount: unsuccess,
          skippedCount: skipped,
          textColorClass: "text-destructive",
          borderColorClass: "border-destructive/20",
        }
      if (unsuccess > 0)
        return {
          errorCount: 0,
          unsuccessCount: unsuccess,
          skippedCount: 0,
          textColorClass: "text-testing-1",
          borderColorClass: "border-testing-1/20",
        }
      if (skipped > 0)
        return {
          errorCount: 0,
          unsuccessCount: 0,
          skippedCount: skipped,
          textColorClass: "text-warning",
          borderColorClass: "border-warning/20",
        }
      if (succeeded > 0)
        return {
          errorCount: 0,
          unsuccessCount: 0,
          skippedCount: 0,
          textColorClass: "text-success",
          borderColorClass: "border-success/20",
        }
      return {
        errorCount: 0,
        unsuccessCount: 0,
        skippedCount: 0,
        textColorClass: "text-muted-foreground",
        borderColorClass: "border-border",
      }
    }, [executions])

  if (executions.length < 2) {
    return executions.map((execution) => (
      <CapabilityExecutionMarker key={execution.id} execution={execution} />
    ))
  }

  const summary = t("conversations.chat.capabilityExecutions.group.summary", {
    count: executions.length,
  })
  const statusSuffix = [
    errorCount > 0
      ? t("conversations.chat.capabilityExecutions.group.errorSuffix", { count: errorCount })
      : null,
    unsuccessCount > 0
      ? t("conversations.chat.capabilityExecutions.group.unsuccessSuffix", {
          count: unsuccessCount,
        })
      : null,
    skippedCount > 0
      ? t("conversations.chat.capabilityExecutions.group.skippedSuffix", { count: skippedCount })
      : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <Collapsible className="w-full flex flex-col gap-4">
      <div
        className={cn(
          "w-full flex items-center justify-center text-xs font-medium relative group",
          textColorClass,
        )}
      >
        <div className={cn("border border-b absolute w-full", borderColorClass)} />
        <CollapsibleTrigger className="flex items-center gap-1.5 px-4 bg-background z-10 relative rounded-full cursor-pointer [&[data-state=open]>svg:last-child]:rotate-180">
          <Layers size={16} className="shrink-0" />
          <span className="text-xs">
            {summary}
            {statusSuffix ? ` · ${statusSuffix}` : null}
          </span>
          <ChevronDown size={16} className="shrink-0 duration-150" />
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="w-full flex flex-col gap-4">
        {executions.map((execution) => (
          <CapabilityExecutionMarker key={execution.id} execution={execution} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}
